import React, { useEffect, useState, useCallback } from 'react';
import {
  getPermissions, createPermission, updatePermission, deletePermission,
} from '../../api/client';
import { Lock, Plus, Edit2, Trash2, X } from 'lucide-react';

const MODULE_OPTIONS = [
  'dashboard', 'work_orders', 'technicians', 'parts',
  'invoices', 'ai_assistant', 'billing', 'system',
];
const TYPE_OPTIONS = ['page', 'button', 'api', 'module'];

const TYPE_BADGE = {
  page:   { bg: '#DBEAFE', color: '#1D4ED8' },
  button: { bg: '#D1FAE5', color: '#065F46' },
  api:    { bg: '#EDE9FE', color: '#5B21B6' },
  module: { bg: '#FEF3C7', color: '#92400E' },
};

function PermModal({ initial, allPerms, onSave, onClose }) {
  const EMPTY = { name: '', code: '', type: 'page', module: 'work_orders', description: '', sort_order: 0, status: true, parent_id: '' };
  const [form, setForm] = useState(initial ? {
    name: initial.name, code: initial.code, type: initial.type,
    module: initial.module, description: initial.description || '',
    sort_order: initial.sort_order, status: initial.status,
    parent_id: initial.parent_id || '',
  } : EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('Name is required'); return; }
    if (!form.code.trim()) { setErr('Code is required'); return; }
    setSaving(true);
    setErr('');
    try {
      const payload = { ...form, parent_id: form.parent_id || null };
      await onSave(payload);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const inp = (label, key, opts = {}) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
      <input
        value={form[key]}
        onChange={e => set(key, opts.number ? Number(e.target.value) : e.target.value)}
        placeholder={opts.placeholder || ''}
        type={opts.number ? 'number' : 'text'}
        style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13 }}
      />
      {opts.hint && <span style={{ fontSize: 11, color: '#9CA3AF' }}>{opts.hint}</span>}
    </div>
  );

  const sel = (label, key, options) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
      <select
        value={form[key]}
        onChange={e => set(key, e.target.value)}
        style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13 }}
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>
            {initial ? 'Edit Permission' : 'New Permission'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
            <X size={18} />
          </button>
        </div>

        {err && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
            {err}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div style={{ gridColumn: '1/-1' }}>{inp('Name', 'name', { placeholder: 'e.g. Create Work Order' })}</div>
          <div style={{ gridColumn: '1/-1' }}>{inp('Permission Code', 'code', { placeholder: 'e.g. work_orders:create', hint: 'Format: module:resource:action — must be unique' })}</div>
          {sel('Module', 'module', MODULE_OPTIONS)}
          {sel('Type', 'type', TYPE_OPTIONS)}
          <div style={{ gridColumn: '1/-1' }}>{inp('Description', 'description', { placeholder: 'Optional' })}</div>
          {inp('Sort Order', 'sort_order', { number: true })}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Status</label>
            <select
              value={form.status ? 'active' : 'inactive'}
              onChange={e => set('status', e.target.value === 'active')}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13 }}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div style={{ gridColumn: '1/-1', marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Parent Permission (optional)</label>
            <select
              value={form.parent_id}
              onChange={e => set('parent_id', e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13 }}
            >
              <option value="">None</option>
              {allPerms.filter(p => !initial || p.id !== initial.id).map(p => (
                <option key={p.id} value={p.id}>{p.code}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '9px 18px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────
export default function Permissions() {
  const [perms, setPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterModule, setFilterModule] = useState('');
  const [modal, setModal] = useState(null); // null | { mode: 'create'|'edit', perm? }
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getPermissions(filterModule || undefined)
      .then(r => setPerms(r.data))
      .finally(() => setLoading(false));
  }, [filterModule]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    if (modal.mode === 'create') {
      await createPermission(form);
    } else {
      await updatePermission(modal.perm.id, form);
    }
    load();
  };

  const handleDelete = async (perm) => {
    await deletePermission(perm.id);
    setDeleteConfirm(null);
    load();
  };

  const grouped = MODULE_OPTIONS.reduce((acc, mod) => {
    const group = (filterModule ? perms : perms).filter(p => p.module === mod);
    if (group.length) acc[mod] = group;
    return acc;
  }, {});

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Lock size={22} color="#2563EB" />
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Permission Management</h1>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select
            value={filterModule}
            onChange={e => setFilterModule(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, background: '#fff' }}
          >
            <option value="">All Modules</option>
            {MODULE_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <button
            onClick={() => setModal({ mode: 'create' })}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            <Plus size={15} /> New Permission
          </button>
        </div>
      </div>

      {/* Info */}
      <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: '#1D4ED8' }}>
        <strong>{perms.length}</strong> permissions loaded. Permissions are global and apply across all companies.
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>Loading...</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                {['Code', 'Name', 'Module', 'Type', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {perms.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No permissions found</td></tr>
              ) : (
                perms.map((perm, i) => {
                  const tb = TYPE_BADGE[perm.type] || TYPE_BADGE.page;
                  return (
                    <tr key={perm.id} style={{ borderBottom: i < perms.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                      <td style={{ padding: '10px 16px', fontFamily: 'monospace', fontSize: 12, color: '#374151' }}>{perm.code}</td>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: '#111827', fontWeight: 500 }}>{perm.name}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{ background: '#F3F4F6', color: '#374151', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                          {perm.module}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{ background: tb.bg, color: tb.color, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                          {perm.type}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{ background: perm.status ? '#D1FAE5' : '#FEE2E2', color: perm.status ? '#065F46' : '#991B1B', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                          {perm.status ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => setModal({ mode: 'edit', perm })} style={{ padding: '4px 8px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, cursor: 'pointer', color: '#374151' }}>
                            <Edit2 size={13} />
                          </button>
                          <button onClick={() => setDeleteConfirm(perm)} style={{ padding: '4px 8px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, cursor: 'pointer', color: '#DC2626' }}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <PermModal
          initial={modal.mode === 'edit' ? modal.perm : null}
          allPerms={perms}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Delete Permission?</h3>
            <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 6 }}>
              Delete <code style={{ background: '#F3F4F6', padding: '1px 6px', borderRadius: 4 }}>{deleteConfirm.code}</code>?
            </p>
            <p style={{ fontSize: 13, color: '#EF4444', marginBottom: 20 }}>This will also remove it from all role assignments.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: '9px 18px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} style={{ padding: '9px 18px', background: '#EF4444', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
